/**
 * Variant Image Filter
 * 
 * This script compares color variant selections with product images
 * and shows/hides slides based on the comparison of the variant's 
 * data-color-name value and image alt text.
 * 
 * Special format support:
 * - Handles alt tags with format: "color || description" 
 * - Handles multiple colors in alt tags: "color1, color2, color3 || description"
 * - Shows images when any selected variant color matches any color in the alt tag
 */
class VariantImageFilter {
  constructor() {
    // Enable debugging to see what's happening in the console
    this.debug = false;
    this.init();
  }

  init() {
    // Initialize when DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupEventListeners();
        this.filterSlidesBasedOnCurrentVariant();
      });
    } else {
      this.setupEventListeners();
      this.filterSlidesBasedOnCurrentVariant();
    }
    
    if (this.debug) {
      console.log('VariantImageFilter initialized');
    }
  }

  setupEventListeners() {
    // Listen to variant changes
    document.addEventListener('variant:change', this.handleVariantChange.bind(this));
    
    // Add click event listeners to variant inputs
    document.querySelectorAll('[data-color-name]').forEach(input => {
      input.addEventListener('change', this.handleVariantSelection.bind(this));
    });

    // Event delegation for handling dynamically added variants
    document.addEventListener('click', (event) => {
      if (event.target.closest('.variant-input') && event.target.closest('.variant-input').querySelector('[data-color-name]')) {
        setTimeout(() => {
          this.filterSlidesBasedOnCurrentVariant();
        }, 100); // Small delay to ensure variant data is updated
      }
    });
  }

  handleVariantChange(event) {
    this.filterSlidesBasedOnCurrentVariant();
  }

  handleVariantSelection(event) {
    this.filterSlidesBasedOnCurrentVariant();
  }

  filterSlidesBasedOnCurrentVariant() {
    // Get all color variant inputs
    const colorVariantInputs = document.querySelectorAll('[data-color-name]');
    const checkedVariants = Array.from(colorVariantInputs).filter(input => {
      if (input.type === 'radio') {
        return input.checked;
      } else if (input.tagName === 'OPTION') {
        return input.selected;
      }
      return false;
    });

    if (checkedVariants.length === 0) {
      if (this.debug) console.log('No checked variant found');
      return;
    }

    // Get the selected color variant value
    const selectedColorValue = checkedVariants[0].getAttribute('data-color-name');
    if (!selectedColorValue) {
      if (this.debug) console.log('No data-color-name attribute found');
      return;
    }

    if (this.debug) console.log('Selected color variant:', selectedColorValue);

    // Get all main image slides
    const mainImageSlides = document.querySelectorAll('.product-gallery-swiper .swiper-slide');
    
    // Get the image thumbs
    const thumbSlides = document.querySelectorAll('.thumbs-swiper .product__thumb-item');
    
    if (this.debug) {
      console.log('Found', mainImageSlides.length, 'main slides');
      console.log('Found', thumbSlides.length, 'thumb slides');
    }
    
    // Create arrays to track which slides should be visible
    let visibleMainSlides = [];
    let visibleThumbSlides = [];

    // Extract all selected variant colors
    const selectedColors = this.extractColors(selectedColorValue);
    if (this.debug) console.log('Extracted selected colors:', selectedColors);

    // Check each thumb for matching alt text
    thumbSlides.forEach((thumbSlide, index) => {
      const thumbImg = thumbSlide.querySelector('img');
      if (!thumbImg) return;
      
      const altText = thumbImg.getAttribute('alt') || '';
      // Check if the alt text contains the selected color value
      const isMatch = this.checkAltTextMatch(altText, selectedColors);
      
      if (this.debug) {
        console.log(`Slide ${index} alt text:`, altText);
        console.log(`Slide ${index} match result:`, isMatch);
      }
      
      // Store the visible state
      visibleThumbSlides[index] = isMatch;
      if (mainImageSlides[index]) {
        visibleMainSlides[index] = isMatch;
      }
    });

    // Apply visibility to thumb slides
    let visibleCount = 0;
    thumbSlides.forEach((slide, index) => {
      if (visibleThumbSlides[index]) {
        slide.style.display = '';
        visibleCount++;
      } else {
        slide.style.display = 'none';
      }
    });

    // If no slides are visible, make sure at least the first slide is visible
    // This prevents an empty gallery if no matches are found
    if (visibleCount === 0 && thumbSlides.length > 0) {
      thumbSlides[0].style.display = '';
      if (mainImageSlides[0]) {
        mainImageSlides[0].style.display = '';
      }
      visibleCount = 1;
      
      if (this.debug) console.log('No matches found, showing the first slide as fallback');
    }

    if (this.debug) console.log(`Made ${visibleCount} slides visible out of ${thumbSlides.length}`);

    // Apply visibility to main image slides
    mainImageSlides.forEach((slide, index) => {
      if (visibleMainSlides[index]) {
        slide.style.display = '';
      } else {
        slide.style.display = 'none';
      }
    });

    // Update the swiper instances to reflect the changes
    this.updateSwiperInstances();
  }
  
  extractColors(colorString) {
    if (!colorString) return [];
    
    // Split the colorString in case it contains multiple options
    // and normalize them (trim whitespace and convert to lowercase)
    return colorString.split(',')
      .map(v => v.trim().toLowerCase())
      .filter(v => v.length > 0); // Filter out empty strings
  }

  checkAltTextMatch(altText, selectedColors) {
    if (!altText || selectedColors.length === 0) return false;
    
    // Normalize the alt text
    const normalizedAltText = altText.toLowerCase();
    
    // Extract colors from alt text (before || if it exists)
    const altColorPart = normalizedAltText.includes('||') ? 
      normalizedAltText.split('||')[0].trim() : 
      normalizedAltText;
    
    // Extract and normalize the colors in the alt text
    const altColors = this.extractColors(altColorPart);
    
    if (this.debug) {
      console.log('Alt text:', altText);
      console.log('Alt color part:', altColorPart);
      console.log('Extracted alt colors:', altColors);
    }
    
    // If there are no colors in the alt text, don't match
    if (altColors.length === 0) return false;
    
    // Check if any of the selected colors matches any of the alt text colors
    const matchResult = selectedColors.some(selectedColor => {
      return altColors.some(altColor => {
        // Try different matching strategies
        const exactMatch = altColor === selectedColor;
        const altColorIncludesSelected = altColor.includes(selectedColor);
        const selectedIncludesAltColor = selectedColor.includes(altColor);
        
        if (this.debug && (exactMatch || altColorIncludesSelected || selectedIncludesAltColor)) {
          console.log(`Match found: "${selectedColor}" matches with "${altColor}"`);
          if (exactMatch) console.log('Exact match');
          if (altColorIncludesSelected) console.log('Alt color includes selected');
          if (selectedIncludesAltColor) console.log('Selected includes alt color');
        }
        
        return exactMatch || altColorIncludesSelected || selectedIncludesAltColor;
      });
    });
    
    return matchResult;
  }

  updateSwiperInstances() {
    // If Swiper instances exist in the global scope, update them
    if (window.swiper && typeof window.swiper.update === 'function') {
      window.swiper.update();
    }
    
    if (window.swiper2 && typeof window.swiper2.update === 'function') {
      window.swiper2.update();
      
      // Navigate to the first visible slide
      const visibleSlide = document.querySelector('.product-gallery-swiper .swiper-slide:not([style*="display: none"])');
      if (visibleSlide) {
        const index = Array.from(visibleSlide.parentNode.children).indexOf(visibleSlide);
        if (index >= 0) {
          window.swiper2.slideTo(index, 0);
          // Also update the thumb swiper to show the corresponding thumbnail
          if (window.swiper) {
            window.swiper.slideTo(index, 0);
          }
        }
      }
    }
  }
}

// Initialize the variant image filter
new VariantImageFilter();
